import { IAgentStatusDeprecatedService } from '@agent/core/agent/agent-status-deprecated.service';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { ChannelDeprecatedImpl } from '@commons/models/channel';
import { IContact } from '@commons/models/contact';
import { IDeferred, ILogService, IPromise, IQService } from 'angular';
import { filter } from 'rxjs';

import {
  ActivityFacade,
  ActivityStatus,
  ActivityType,
  AgentSkillFacade,
  AgentStatus,
  ChanEnum,
  Configuration,
  isNotNullOrUndefined,
  PositionFacade,
} from '@contact/upgrade';

const MODULE_NAME = 'osv';

export interface IOsvService {
  logon(): IPromise<any>;

  logout(): IPromise<any>;
}

export class OsvService implements IOsvService {
  static $inject: Array<string> = [
    '$q',
    '$log',
    'agentStatusDeprecatedService',
    'activityFacade',
    'configuration',
    'toastDeprecatedService',
    'contactDeprecatedService',
    'positionFacade',
    'agentSkillFacade',
  ];

  public name = MODULE_NAME;

  private osvEvents = {
    OPEN_CONNECTION_EVENT: 'openConnectionEvent',
    MAIN_LOGON: 'Main/Logon',
    MAIN_LOGOFF: 'Main/Logoff',
    BLF_MAKE_CALL: 'Blf/MakeCall',
    BLF_CLEAR_CALL: 'Blf/ClearCall',
    BLF_DIRECT_VOICE_CONTACT: 'Blf/DirectVoiceContact',
    BLF_START_MONITORING: 'Blf/StartMonitoring',
    BLF_STOP_MONITORING: 'Blf/StopMonitoring',
    BLF_CTI_STATE: 'Blf/CtiState',
    PING: 'Ping',
    UNKNOWN: 'unknownEvent',
  };

  private osvEventState = {
    IDLE: 0,
    ALERTING: 1,
    BUSY: 2,
    UNKNOWN: 3,
  };

  private currentStatus: number = this.osvEventState.IDLE;
  private connectionLost: boolean = false;

  private osvWebSocket: any;
  private osvReconnectNumber: number = 5;
  private osvUserToken: string | null;

  private userPositionPhoneNumber: string;
  private inProgress: boolean = false;

  constructor(
    private readonly $q: IQService,
    private readonly $log: ILogService,
    private readonly agentStatusDeprecatedService: IAgentStatusDeprecatedService,
    private readonly activityFacade: ActivityFacade,
    private readonly configuration: Configuration,
    private readonly toastDeprecatedService: ToastDeprecatedService,
    private readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly positionFacade: PositionFacade,
    private readonly agentSkillFacade: AgentSkillFacade
  ) {
    // position phone number
    this.positionFacade.position$.pipe(isNotNullOrUndefined()).subscribe(({ uri }) => (this.userPositionPhoneNumber = uri));

    /**
     * listen to pause state success
     */
    this.activityFacade.success$
      .pipe(
        // if type is OSV
        filter(({ activity }) => activity.type === ActivityType.OSV),
        // if status is CANCELED
        filter(({ current }) => current === ActivityStatus.CANCELED)
      )
      .subscribe(({ id }) => {
        this.activityFacade.updateTransition(id, ActivityStatus.FINISHED);
        this.$log.debug('[osv] pause finished');
      });

    // OSV is in progress
    this.activityFacade.isTypeInprogress(ActivityType.OSV).subscribe(inProgress => (this.inProgress = inProgress));
  }

  // logon to Unify
  public logon(deferred: IDeferred<any> = this.$q.defer()): IPromise<any> {
    if (this.configuration.osv && this.configuration.osv.url) {
      this.$log.debug(
        '[osv] logon url = ' +
          this.configuration.osv.url +
          ', tenant id = ' +
          this.configuration.osv.tenantId +
          ', login = ' +
          this.configuration.osv.login +
          ', password = ' +
          this.configuration.osv.password
      );

      this.osvWebSocket = new WebSocket(this.configuration.osv.url);
      this.osvWebSocket.addEventListener('error', e => {
        // ReadyStates: CONNECTING OPEN CLOSING or CLOSED
        if (e.target.readyState === 3) {
          if (--this.osvReconnectNumber > 0) {
            setTimeout(() => this.logon(deferred), 5000); // on error we try to reconnect at most "osvReconnectNumber" times
          } else {
            throw new Error('[osv] Max Retry Connection Count reached!');
          }
        }
      });

      this.osvWebSocket.onopen = this.onOpen.bind(this);
      this.osvWebSocket.onmessage = this.onMessage.bind(this);
      this.osvWebSocket.onclose = this.onClose.bind(this);
      deferred.resolve();
    } else {
      deferred.reject();
    }
    return deferred.promise;
  }

  // logout from Unify
  public logout(): IPromise<any> {
    let deferred = this.$q.defer();
    if (!this.osvUserToken) {
      deferred.reject();
    } else {
      let logoffData = {
        Body: {
          AuthStatement: this.osvUserToken,
        },
        Head: {
          Id: this.osvEvents.MAIN_LOGOFF,
          InvokeId: this.getNextInvokeId()(),
          Type: 'Request',
          AuthStatement: '',
        },
      };
      this.send(logoffData);
      deferred.resolve();
    }

    return deferred.promise;
  }

  private send(data) {
    let sendData = JSON.stringify(data);
    this.osvWebSocket.send(sendData);
    this.$log.debug('[osv] send: ' + sendData);
  }

  private getNextInvokeId() {
    let i = 0;
    return () => {
      if (i > 9999) {
        i = 0;
      }
      return Array(4 - String(i).length + 1).join('0') + i++;
    };
  }

  private onOpen() {
    this.$log.debug('[osv] Socket Status: ' + this.osvWebSocket.readyState + ' (open)');
    this.osvReconnectNumber = 5;
    let logonData = {
      Body: {
        UserId: this.configuration.osv?.login,
        Tenant: this.configuration.osv?.tenantId,
        Extension: '',
        Password: this.configuration.osv?.password,
        ApplicationIdent: this.configuration.osv?.applicationId,
      },
      Head: {
        Id: this.osvEvents.MAIN_LOGON,
        InvokeId: this.getNextInvokeId()(),
        Type: 'Request',
        AuthStatement: '',
      },
    };
    this.send(logonData);
  }

  private startMonitoring() {
    let startMonitoringBlf = {
      Body: {
        Extension: this.userPositionPhoneNumber,
      },
      Head: {
        Id: this.osvEvents.BLF_START_MONITORING,
        InvokeId: this.getNextInvokeId()(),
        Type: 'Request',
      },
    };
    this.send(startMonitoringBlf);
  }

  private stopMonitoring() {
    let stopMonitoringBlf = {
      Body: {
        Extension: this.userPositionPhoneNumber,
      },
      Head: {
        Id: this.osvEvents.BLF_STOP_MONITORING,
        InvokeId: this.getNextInvokeId()(),
        Type: 'Request',
      },
    };
    this.send(stopMonitoringBlf);
  }

  private ping(invokeId) {
    let pingBlf = {
      Body: {},
      Head: {
        Id: this.osvEvents.PING,
        InvokeId: invokeId,
        Type: 'Response',
      },
    };
    this.send(pingBlf);
  }

  private onMessage(msg) {
    this.$log.debug('[osv] Received: ' + msg.data);
    let _data = JSON.parse(msg.data);
    this.updateAgentState(_data.Head.Id, _data);
  }

  private onClose() {
    this.$log.debug('[osv] Socket Status: ' + this.osvWebSocket.readyState + ' (Closed)');
  }

  private updateStateVoiceSkills(state: boolean) {
    this.agentSkillFacade.changeAllActivation(ChanEnum.In.Phone, state);
    this.agentSkillFacade.changeAllActivation(ChanEnum.Out.Phone, state);
  }

  private updateAgentState(type: string, data: any): void {
    let currentStatusContact = this.agentStatusDeprecatedService.getCurrentAgentState();
    switch (type) {
      case this.osvEvents.MAIN_LOGON:
        this.osvUserToken = data.Body.AuthStatement;
        if (this.connectionLost) {
          this.toastDeprecatedService.notify('agent:toast.osv_login_reconnect', 5000);
          this.connectionLost = false;
          this.updateStateVoiceSkills(true);
        }
        this.startMonitoring();
        break;
      case this.osvEvents.MAIN_LOGOFF:
        this.osvUserToken = null;
        this.toastDeprecatedService.alert('agent:toast.osv_login_failed');
        this.connectionLost = true;
        this.updateStateVoiceSkills(false);
        this.stopMonitoring();
        break;
      case this.osvEvents.BLF_MAKE_CALL:
        break;
      case this.osvEvents.BLF_CLEAR_CALL:
        break;
      case this.osvEvents.BLF_DIRECT_VOICE_CONTACT:
        break;
      case this.osvEvents.BLF_START_MONITORING:
        break;
      case this.osvEvents.BLF_STOP_MONITORING:
        break;
      case this.osvEvents.PING:
        this.ping(data.Head.InvokeId);
        break;
      case this.osvEvents.BLF_CTI_STATE:
        if (data.Body.Extension === this.userPositionPhoneNumber) {
          switch (data.Body.BlfCtiState) {
            case this.osvEventState.IDLE:
              if (currentStatusContact === AgentStatus.WORKING && !this.inProgress) {
                let contacts: IContact[] = this.contactDeprecatedService.getContactsByChannel(ChannelDeprecatedImpl.INCOMING_PHONE);
                if (contacts && contacts.length > 1) {
                  this.$log.error("[osv] Impossible de couper l'appel");
                } else {
                  this.contactDeprecatedService.close(contacts[0].responseId);
                }
                this.$log.debug("[osv] Fin d'appel");
              }
              if (this.currentStatus === this.osvEventState.ALERTING || this.currentStatus === this.osvEventState.BUSY) {
                if (currentStatusContact === AgentStatus.NOT_WORKING) {
                  // Set agent available if he's in circuit pause
                  this.activityFacade.updateTransitionByType(ActivityType.OSV, ActivityStatus.FINISHED);
                  this.$log.debug('[osv] pause ended');
                } else {
                  if (this.inProgress) {
                    // Cancel pause
                    this.activityFacade.updateTransitionByType(ActivityType.OSV, ActivityStatus.CANCELED);
                    this.$log.debug('[osv] pause canceled');
                  }
                }
              }
              break;
            case this.osvEventState.ALERTING:
            case this.osvEventState.BUSY:
              if (this.currentStatus === this.osvEventState.IDLE) {
                // status is "do not disturb" means pause or "busy" means on call
                // Set agent in pause if he's available
                this.activityFacade.openForm(ActivityType.OSV);
              }
              break;
            case this.osvEventState.UNKNOWN:
              this.$log.error('[osv] Unknown event with body: ' + data.Body);
              break;
            default:
              this.$log.error('[osv] Default event with body: ' + data.Body);
          }
          this.currentStatus = data.Body.BlfCtiState;
        }
        break;
      default:
        this.$log.error("[osv] Unknown event with type '" + type + "' and data: " + data);
    }
  }
}

export default MODULE_NAME;
